import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Whisper, Tooltip } from "rsuite";
import "./TruncatedText.scss";

// Renders `text` with CSS ellipsis. Mounts a Whisper tooltip only when the
// element actually overflows its container — measured at layout time and
// re-measured on container resize. Avoids the character-count guesswork that
// silently misfires with variable-width fonts.
const TruncatedText = ({ text, className, placeholder = "-" }) => {
  const ref = useRef(null);
  const [isOverflowing, setOverflowing] = useState(false);

  const display = text || placeholder;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      setOverflowing(el.scrollWidth > el.clientWidth);
    };
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // isOverflowing is a dep because toggling it rewraps the span in Whisper,
    // remounting it — the observer must re-attach to the new node.
  }, [display, isOverflowing]);

  const classes = ["ht-truncated-text", className].filter(Boolean).join(" ");
  const node = (
    <span ref={ref} className={classes}>
      {display}
    </span>
  );

  if (!isOverflowing || display === placeholder) return node;

  return (
    <Whisper
      placement="top"
      trigger="hover"
      speaker={<Tooltip>{display}</Tooltip>}
    >
      {node}
    </Whisper>
  );
};

TruncatedText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  placeholder: PropTypes.string,
};

export default TruncatedText;
